import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Share2, Check, Sparkles, Calendar } from 'lucide-react';
import ConsultationScheduler from '../components/ConsultationScheduler';

const AcademySuccess = () => {
  const [searchParams] = useSearchParams();
  const [showSocialCard, setShowSocialCard] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [copied, setCopied] = useState(false);
  const [consultationBooked, setConsultationBooked] = useState(false);
  
  // Get student data from URL params or localStorage
  const studentName = searchParams.get('name') || localStorage.getItem('academy_student_name') || 'Scholar Athlete';
  const studentZone = searchParams.get('zone') || localStorage.getItem('academy_student_zone') || 'elite';
  
  const getZoneDisplay = (zone) => {
    const zones = {
      clogging: { label: 'CLOGGING ZONE', color: '#EF4444' },
      coasting: { label: 'COASTING ZONE', color: '#F59E0B' },
      elite: { label: 'ELITE ZONE', color: '#D4AF37' }
    };
    return zones[zone] || zones.elite;
  };

  const zoneInfo = getZoneDisplay(studentZone);

  const handleShare = async () => {
    const shareData = {
      title: 'I Just Enrolled in the Pace & Pivot Course!',
      text: `${studentName} has officially enrolled at Scholar Athlete Academy. The first move is the pivot. #PRSVR #ScholarAthlete #PaceAndPivot`,
      url: window.location.origin + '/academy'
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        setShowSocialCard(true);
      }
    } else {
      setShowSocialCard(true);
    }
  };

  const copyToClipboard = () => {
    const text = `🏀 I just enrolled in the Pace & Pivot course at Scholar Athlete Academy!\n\nThe first move is the pivot.\n\n${studentName} | ${zoneInfo.label}\n\n#PRSVR #ScholarAthlete #PaceAndPivot\n\n${window.location.origin}/academy`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleBookingComplete = (details) => {
    setConsultationBooked(true);
    setShowScheduler(false);
  };

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      {/* Full-screen Video Background with Overlay */}
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background - Using animated gradient as placeholder */}
        <div className="absolute inset-0">
          {/* Dark gradient background with pulse animation */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900">
            {/* Animated pulse overlay */}
            <div className="absolute inset-0 animate-pulse-slow">
              <div className="absolute inset-0 bg-gradient-to-t from-orange-600/5 via-transparent to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/80"></div>
            </div>
            
            {/* Subtle grid pattern for texture */}
            <div 
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage: 'linear-gradient(rgba(255,87,34,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,87,34,0.3) 1px, transparent 1px)',
                backgroundSize: '40px 40px'
              }}
            ></div>
          </div>
          
          {/* Dark overlay to make text pop */}
          <div className="absolute inset-0 bg-black/60"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 text-center py-24">
          {/* Sparkle animation */}
          <div className="inline-block mb-6 animate-bounce">
            <Sparkles className="w-12 h-12 text-orange-600" />
          </div>
          
          {/* Main Headline */}
          <h1 className="text-6xl md:text-8xl lg:text-9xl font-black mb-6 leading-none tracking-tight">
            <span className="text-white">ADMISSIONS</span>
            <br />
            <span 
              className="bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent"
              style={{ 
                textShadow: '0 0 60px rgba(255,87,34,0.5)',
                WebkitTextStroke: '2px rgba(255,87,34,0.3)'
              }}
            >
              CONFIRMED
            </span>
          </h1>

          {/* Student Name */}
          <div className="mb-8">
            <p className="text-2xl md:text-3xl text-gray-400 font-light mb-2">Welcome to the Academy</p>
            <p className="text-4xl md:text-5xl font-black text-white">{studentName}</p>
          </div>

          {/* Zone Badge */}
          <div className="inline-block mb-12">
            <div 
              className="px-8 py-4 border-2 rounded-none font-black text-xl tracking-widest"
              style={{ 
                borderColor: zoneInfo.color,
                color: zoneInfo.color,
                backgroundColor: `${zoneInfo.color}10`
              }}
            >
              {zoneInfo.label}
            </div>
          </div>

          {/* Tagline */}
          <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-2xl mx-auto">
            The first move is the pivot. Your journey to academic and athletic excellence starts now.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button 
              onClick={handleShare}
              className="bg-orange-600 hover:bg-orange-700 text-white font-black text-lg px-12 py-6 rounded-none inline-flex items-center gap-3"
            >
              <Share2 className="w-6 h-6" />
              Share Your Commitment
            </Button>
            
            <Button 
              onClick={() => setShowScheduler(true)}
              variant="outline"
              className={`border-2 font-black text-lg px-12 py-6 rounded-none inline-flex items-center gap-3 ${
                consultationBooked 
                  ? 'border-green-600 text-green-600 bg-green-600/10' 
                  : 'border-white text-white hover:bg-white hover:text-black'
              }`}
            >
              {consultationBooked ? (
                <>
                  <Check className="w-6 h-6" />
                  Consultation Booked
                </>
              ) : (
                <>
                  <Calendar className="w-6 h-6" />
                  Book Consultation
                </>
              )}
            </Button>
          </div>

          {/* Next Steps */}
          <div className="max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-gray-400 mb-6 uppercase tracking-wider">What's Next?</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/5 border border-gray-800 p-6 text-center">
                <div className="text-3xl font-black text-orange-600 mb-2">01</div>
                <p className="text-sm text-gray-400">Check your email for your Letter of Intent</p>
              </div>
              <div className="bg-white/5 border border-gray-800 p-6 text-center">
                <div className="text-3xl font-black text-orange-600 mb-2">02</div>
                <p className="text-sm text-gray-400">Complete the Day 2 Playbook challenge</p>
              </div>
              <div className="bg-white/5 border border-gray-800 p-6 text-center">
                <div className="text-3xl font-black text-orange-600 mb-2">03</div>
                <p className="text-sm text-gray-400">Join us January 8th for orientation</p>
              </div>
            </div>
          </div>

          {/* Back to Academy Link */}
          <div className="mt-12">
            <Link 
              to="/academy" 
              className="text-gray-500 hover:text-orange-600 transition-colors text-sm uppercase tracking-wider"
            >
              ← Back to Academy
            </Link>
          </div>
        </div>
      </div>

      {/* Consultation Scheduler Modal */}
      <Dialog open={showScheduler} onOpenChange={setShowScheduler}>
        <DialogContent className="bg-gray-900 border-gray-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl font-black flex items-center gap-3">
              <Calendar className="w-6 h-6 text-orange-600" />
              Schedule a <span className="text-orange-600">Consultation</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            <p className="text-gray-400 text-sm mb-6">
              Book a free 30-minute consultation with our admissions team to discuss {studentName}'s academic journey.
            </p>
            
            <ConsultationScheduler 
              athleteName={studentName}
              readinessZone={studentZone}
              onBookingComplete={handleBookingComplete}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Social Card Modal */}
      <Dialog open={showSocialCard} onOpenChange={setShowSocialCard}>
        <DialogContent className="bg-gray-900 border-gray-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl font-black">Share Your Commitment</DialogTitle>
          </DialogHeader>
          
          {/* Social Card Preview */}
          <div className="relative">
            <div 
              className="aspect-square bg-black border-2 border-orange-600 p-8 flex flex-col items-center justify-center text-center"
              id="social-card"
            >
              {/* Logo at top - Large */}
              <div className="mb-8">
                <img 
                  src="/images/scholar-athlete-logo.png" 
                  alt="PRSVR Scholar Athlete Academy" 
                  className="h-32 w-auto"
                  style={{ filter: 'invert(37%) sepia(100%) saturate(2500%) hue-rotate(2deg) brightness(105%) contrast(105%)' }}
                />
              </div>
              
              <div className="text-3xl font-black text-white mb-4">{studentName}</div>
              
              <div 
                className="px-4 py-2 text-sm font-bold tracking-wider"
                style={{ 
                  backgroundColor: zoneInfo.color,
                  color: 'white'
                }}
              >
                {zoneInfo.label}
              </div>
              
              <div className="mt-6 text-gray-500 text-xs uppercase tracking-wider">
                Pace & Pivot Course • The PRSVR School
              </div>
            </div>
          </div>

          {/* Share Actions */}
          <div className="flex flex-col gap-3 mt-4">
            <Button 
              onClick={copyToClipboard}
              className="w-full bg-white/10 hover:bg-white/20 text-white border border-gray-700"
            >
              {copied ? <Check className="w-4 h-4 mr-2" /> : null}
              {copied ? 'Copied!' : 'Copy to Clipboard'}
            </Button>
            <div className="flex gap-3">
              <Button 
                onClick={() => {
                  // Open Twitter/X share with proper mobile handling
                  const text = encodeURIComponent(`🏀 I just enrolled in the Pace & Pivot course at Scholar Athlete Academy! The first move is the pivot. #PRSVR #ScholarAthlete`);
                  const url = encodeURIComponent(window.location.origin + '/academy');
                  const twitterUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
                  // Use location.href for better mobile compatibility
                  window.location.href = twitterUrl;
                }}
                className="flex-1 bg-black hover:bg-gray-900 text-white border border-gray-700"
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                Share on X
              </Button>
              <Button 
                onClick={() => {
                  // Copy text to clipboard first
                  const shareText = `🏀 I just enrolled in the Pace & Pivot course at Scholar Athlete Academy! The first move is the pivot. #PRSVR #ScholarAthlete\n\n${window.location.origin}/academy`;
                  navigator.clipboard.writeText(shareText).then(() => {
                    // Show toast notification
                    alert('Caption copied to clipboard! Opening Instagram...');
                    // Try to open Instagram app on mobile, fallback to web
                    setTimeout(() => {
                      window.location.href = 'instagram://app';
                      // Fallback to web after a short delay if app doesn't open
                      setTimeout(() => {
                        window.open('https://www.instagram.com/', '_blank');
                      }, 1500);
                    }, 500);
                  });
                }}
                className="flex-1 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 hover:from-purple-700 hover:via-pink-600 hover:to-orange-600 text-white"
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                Share on Instagram
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom animation styles */}
      <style jsx>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default AcademySuccess;
